/**
 * ProximasPage — Reservas con salida futura (fecha > hoy y estado != hold).
 *
 * Vista operativa con: presets de rango, filtro por agencia y paquete,
 * chips de estado multi-select con contadores, KPI bar, agrupación por día
 * colapsable, búsqueda global y timeline visual por día.
 *
 * Mantiene la funcionalidad de borrar reserva (BorrarReservaDialog) sujeta
 * al cutoff configurado — heredada del outlet como `horaCutoff`.
 */
import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, TextField, MenuItem, Button, Chip, Stack,
  FormControl, InputLabel, Select, IconButton, Collapse,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import BarChartIcon from '@mui/icons-material/BarChart';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ReservacionesTable from './shared/ReservacionesTable';
import VoyageCard from './shared/VoyageCard';
import ReservaSlideOver from './ReservaSlideOver';
import BorrarReservaDialog from './shared/BorrarReservaDialog';
import { categorizar } from './shared/useReservaciones';
import { ESTADO_LABELS } from './shared/EstadoChip';
import { todayLocal, API_BASE, getEmpresaId, getAuthHeaders, formatPrecio, norm } from './shared/format';
import { exportarReservasCSV } from './shared/exportCSV';
import { agruparPorSalida } from './shared/agruparPorSalida';

// Persistir la preferencia del toggle Tarjetas/Tabla entre sesiones,
// igual que HoyPage. Clave distinta para no interferir con la de "Hoy".
const VIEW_STORAGE_KEY = 'nara.booking.proximas.view';
function loadInitialView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'table' ? 'table' : 'cards';
  } catch { return 'cards'; }
}
function saveView(v) {
  try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* no-op */ }
}

// Estados que aparecen naturalmente en Próximas (hold va a su propia pestaña,
// completada/cancelada/no_show al Histórico).
const ESTADOS_PROX = ['pendiente', 'confirmada', 'suspendida'];

// Colores por estado (mismos que usa EstadoChip.jsx)
const TIMELINE_COLORS = {
  pendiente:  '#f59e0b',
  confirmada: '#3b82f6',
  suspendida: '#eab308',
};

// ───────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ───────────────────────────────────────────────────────────────────────────

function _localStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function _sumar(dias) {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return _localStr(d);
}
function _finMes(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset + 1);
  d.setDate(0);
  return _localStr(d);
}
function _inicioMes(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return _localStr(d);
}

const PRESETS = [
  { id: 'manana',       label: 'Mañana',           desde: () => _sumar(1),   hasta: () => _sumar(1) },
  { id: 'sem',          label: 'Próximos 7 días',  desde: () => todayLocal(), hasta: () => _sumar(7) },
  { id: 'quincena',     label: 'Próximos 15 días', desde: () => todayLocal(), hasta: () => _sumar(15) },
  { id: 'este_mes',     label: 'Este mes',         desde: () => todayLocal(), hasta: () => _finMes(0) },
  { id: 'proximo_mes',  label: 'Próximo mes',      desde: () => _inicioMes(1), hasta: () => _finMes(1) },
  { id: 'todo',         label: 'Todo',             desde: () => '',           hasta: () => '' },
];

// Label legible de una fecha YYYY-MM-DD (usada en la agrupación por día)
const DIAS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
function _labelDia(yyyy_mm_dd) {
  if (!yyyy_mm_dd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyy_mm_dd)) return yyyy_mm_dd;
  const hoy = todayLocal();
  if (yyyy_mm_dd === hoy) return 'Hoy';
  if (yyyy_mm_dd === _sumar(1)) return 'Mañana';
  const d = new Date(yyyy_mm_dd + 'T00:00:00');
  const dia = DIAS_ES[d.getDay()];
  const mes = MESES_ES[d.getMonth()];
  const num = d.getDate();
  return `${dia[0].toUpperCase()}${dia.slice(1)} ${num} · ${mes} ${d.getFullYear()}`;
}

/**
 * Agrupa reservas por día de salida. Retorna array ordenado por fecha ASC
 * (más próximas primero — inverso al histórico). Cada grupo con KPIs
 * pre-calculados y reservas ordenadas por hora dentro del día.
 */
function agruparPorDia(rows) {
  const buckets = new Map();
  for (const r of rows) {
    const key = String(r.salida_fecha || '').slice(0, 10) || 'sin-fecha';
    if (!buckets.has(key)) {
      buckets.set(key, {
        dia: key,
        label: key === 'sin-fecha' ? 'Sin fecha' : _labelDia(key),
        reservas: [],
      });
    }
    buckets.get(key).reservas.push(r);
  }
  const arr = Array.from(buckets.values());
  arr.sort((a, b) => a.dia.localeCompare(b.dia));   // ASC: hoy → mañana → ...
  for (const g of arr) {
    g.reservas.sort((a, b) => (a.salida_hora || '').localeCompare(b.salida_hora || ''));
    const pax = g.reservas.reduce((s, r) =>
      s + (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0), 0);
    const neto = g.reservas.reduce((s, r) =>
      s + Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0), 0);
    const porCobrar = g.reservas.reduce((s, r) => {
      const saldo = Number(r.saldo_pendiente || 0);
      const esCxc = (r.saldo_a_cargo_de || '') === 'agencia';
      return s + (saldo > 0 && !esCxc ? saldo : 0);
    }, 0);
    g.kpis = { num: g.reservas.length, pax, neto, porCobrar };
  }
  return arr;
}

// ───────────────────────────────────────────────────────────────────────────
// Componentes visuales
// ───────────────────────────────────────────────────────────────────────────

function KpiTile({ label, value, sub, icon, color = '#0891b2' }) {
  return (
    <Box sx={{
      position: 'relative',
      p: '10px 14px',
      bgcolor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.3,
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 2, background: color, opacity: 0.85,
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 14, color } })}
        <Typography sx={{
          fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase',
          fontWeight: 700, color: '#475569',
        }}>{label}</Typography>
      </Box>
      <Typography sx={{
        fontSize: 18, fontWeight: 800, color: '#0f172a',
        lineHeight: 1.15, letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
      }}>{value}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 11.5, color: '#94a3b8' }}>{sub}</Typography>
      )}
    </Box>
  );
}

/**
 * Timeline por día — barras horizontales apiladas por estado. Ancho relativo
 * al día con más reservas del rango. Click en una barra togglea la expansión
 * del grupo correspondiente.
 */
function TimelineDiaria({ grupos, onClickDia }) {
  const maxReservas = useMemo(
    () => grupos.reduce((m, g) => Math.max(m, g.reservas.length), 0),
    [grupos]
  );
  if (grupos.length === 0) return null;
  return (
    <Box>
      <Stack spacing={0.6}>
        {grupos.map(g => {
          const conteo = { pendiente: 0, confirmada: 0, suspendida: 0 };
          for (const r of g.reservas) if (r.estado in conteo) conteo[r.estado] += 1;
          const total = g.reservas.length;
          const widthPct = maxReservas > 0 ? (total / maxReservas) * 100 : 0;
          return (
            <Box key={g.dia}
              onClick={() => onClickDia?.(g.dia)}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '110px 1fr 40px', sm: '180px 1fr 50px' },
                alignItems: 'center', gap: 1, cursor: 'pointer',
                p: 0.5, borderRadius: 1,
                transition: 'background 0.12s',
                '&:hover': { bgcolor: '#f8fafc' },
              }}>
              <Typography sx={{
                fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {g.label}
              </Typography>
              <Box sx={{
                height: 22, bgcolor: '#f1f5f9', borderRadius: 1, overflow: 'hidden',
                position: 'relative',
              }}>
                <Box sx={{
                  width: `${widthPct}%`, height: '100%',
                  display: 'flex', transition: 'width 0.2s',
                }}>
                  {ESTADOS_PROX.map(est => {
                    const n = conteo[est] || 0;
                    if (n === 0) return null;
                    return (
                      <Box key={est}
                        title={`${ESTADO_LABELS[est]?.label || est}: ${n}`}
                        sx={{
                          flex: n,
                          bgcolor: TIMELINE_COLORS[est],
                          transition: 'opacity 0.15s',
                          '&:hover': { opacity: 0.8 },
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 10, fontWeight: 700,
                          overflow: 'hidden',
                        }}>
                        {(n / total) > 0.15 ? n : ''}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Typography sx={{
                fontSize: 12, fontWeight: 800, color: '#0f172a',
                textAlign: 'right',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontVariantNumeric: 'tabular-nums',
              }}>
                {total}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* Leyenda de colores */}
      <Box sx={{ mt: 1, display: 'flex', gap: 1.5, flexWrap: 'wrap', pl: 0.5 }}>
        {ESTADOS_PROX.map(est => (
          <Box key={est} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: TIMELINE_COLORS[est], borderRadius: 0.3 }} />
            <Typography sx={{ fontSize: 10.5, color: '#475569', fontWeight: 600 }}>
              {ESTADO_LABELS[est]?.label || est}
            </Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic', ml: 'auto' }}>
          Click en una barra para expandir el día ↓
        </Typography>
      </Box>
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Componente principal
// ───────────────────────────────────────────────────────────────────────────

export default function ProximasPage() {
  const { reservaciones, isLoading, refetch, horaCutoff } = useOutletContext();
  const empresaId = getEmpresaId();
  const [detalleId, setDetalleId] = useState(null);
  const [borrarReserva, setBorrarReserva] = useState(null);

  // Filtros
  const [presetId, setPresetId] = useState('sem');   // Próximos 7 días por default
  const [fechaDesde, setFechaDesde] = useState(() => todayLocal());
  const [fechaHasta, setFechaHasta] = useState(() => _sumar(7));
  const [agenciaFiltro, setAgenciaFiltro] = useState('todas');
  const [filtroPaquete, setFiltroPaquete] = useState('');
  const [estadosActivos, setEstadosActivos] = useState(new Set(ESTADOS_PROX));
  const [busqueda, setBusqueda] = useState('');
  const busquedaNorm = norm(busqueda.trim());
  const [agenciasList, setAgenciasList] = useState([]);
  const [timelineAbierto, setTimelineAbierto] = useState(true);
  const [view, setView] = useState(loadInitialView);

  const handleView = (_e, v) => {
    if (!v) return;
    setView(v);
    saveView(v);
  };

  // Cargar lista de agencias una sola vez
  React.useEffect(() => {
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const arr = Array.isArray(list) ? list : (list?.agencias || []);
        setAgenciasList([...arr].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')));
      })
      .catch(() => setAgenciasList([]));
  }, [empresaId]);

  const { proximas } = categorizar(reservaciones);

  // Aplicar preset → actualiza fechas
  const aplicarPreset = (id) => {
    setPresetId(id);
    const p = PRESETS.find(x => x.id === id);
    if (!p) return;
    setFechaDesde(p.desde());
    setFechaHasta(p.hasta());
  };
  const onFechaManual = (setter) => (val) => {
    setter(val);
    setPresetId('custom');
  };
  const toggleEstado = (est) => {
    setEstadosActivos(prev => {
      const s = new Set(prev);
      if (s.has(est)) s.delete(est); else s.add(est);
      return s;
    });
  };

  // Paquetes únicos para el selector — sobre TODAS las próximas para no
  // reducir el dropdown cuando el operador cambia otros filtros.
  const paquetes = useMemo(() => {
    const set = new Set();
    proximas.forEach(r => {
      const p = r.paquete_nombre || r.alquiler_nombre;
      if (p) set.add(p);
    });
    return Array.from(set).sort();
  }, [proximas]);

  // Filtrado por fecha + agencia + paquete (sin estado ni búsqueda — se
  // aplican después para que los contadores de chips reflejen este subset).
  const filtradasBase = useMemo(() => {
    return proximas.filter(r => {
      const fecha = String(r.salida_fecha || '').slice(0, 10);
      if (fechaDesde && fecha < fechaDesde) return false;
      if (fechaHasta && fecha > fechaHasta) return false;
      if (agenciaFiltro !== 'todas' && String(r.agencia_id) !== String(agenciaFiltro)) return false;
      if (filtroPaquete && (r.paquete_nombre || r.alquiler_nombre) !== filtroPaquete) return false;
      return true;
    });
  }, [proximas, fechaDesde, fechaHasta, agenciaFiltro, filtroPaquete]);

  // Conteos por estado (subset filtrado por fecha/agencia/paquete + búsqueda)
  const conteosEstado = useMemo(() => {
    const out = Object.fromEntries(ESTADOS_PROX.map(e => [e, 0]));
    const base = busquedaNorm
      ? filtradasBase.filter(r => (
          norm(r.codigo_reserva).includes(busquedaNorm) ||
          norm(r.nombre_cliente).includes(busquedaNorm) ||
          norm(r.numero_whatsapp).includes(busquedaNorm) ||
          norm(r.correo).includes(busquedaNorm) ||
          norm(r.agencia_nombre).includes(busquedaNorm) ||
          norm(r.paquete_nombre).includes(busquedaNorm)
        ))
      : filtradasBase;
    for (const r of base) if (r.estado in out) out[r.estado] += 1;
    return out;
  }, [filtradasBase, busquedaNorm]);

  // Filtro final: estado + búsqueda
  const filtered = useMemo(() => {
    let arr = filtradasBase.filter(r => estadosActivos.has(r.estado));
    if (busquedaNorm) {
      arr = arr.filter(r => (
        norm(r.codigo_reserva).includes(busquedaNorm) ||
        norm(r.nombre_cliente).includes(busquedaNorm) ||
        norm(r.numero_whatsapp).includes(busquedaNorm) ||
        norm(r.correo).includes(busquedaNorm) ||
        norm(r.agencia_nombre).includes(busquedaNorm) ||
        norm(r.paquete_nombre).includes(busquedaNorm)
      ));
    }
    return arr;
  }, [filtradasBase, estadosActivos, busquedaNorm]);

  // Agrupación por día (ASC — más próximos primero)
  const grupos = useMemo(() => agruparPorDia(filtered), [filtered]);

  // Estado de expansión: al buscar, expandir todos con hits; sin búsqueda,
  // solo los primeros 3 días (para no abrumar).
  const [diasExpandidos, setDiasExpandidos] = useState(() => new Set());
  React.useEffect(() => {
    if (grupos.length === 0) {
      setDiasExpandidos(new Set());
    } else if (busquedaNorm) {
      setDiasExpandidos(new Set(grupos.map(g => g.dia)));
    } else {
      setDiasExpandidos(new Set(grupos.slice(0, 3).map(g => g.dia)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupos.map(g => g.dia).join('|'), busquedaNorm]);

  const toggleDia = (dia) => {
    setDiasExpandidos(prev => {
      const s = new Set(prev);
      if (s.has(dia)) s.delete(dia); else s.add(dia);
      return s;
    });
  };
  const expandirTodos = () => setDiasExpandidos(new Set(grupos.map(g => g.dia)));
  const colapsarTodos = () => setDiasExpandidos(new Set());
  const todosExpandidos = grupos.length > 0 && diasExpandidos.size === grupos.length;

  // KPIs globales del subset final
  const kpis = useMemo(() => {
    const num = filtered.length;
    const pax = filtered.reduce((s, r) =>
      s + (r.adultos || 0) + (r.ninos || 0) + (r.bebes || 0), 0);
    const neto = filtered.reduce((s, r) =>
      s + Math.max(Number(r.precio_total || 0) - Number(r.comision_valor || 0), 0), 0);
    const porCobrar = filtered.reduce((s, r) => {
      const saldo = Number(r.saldo_pendiente || 0);
      const esCxc = (r.saldo_a_cargo_de || '') === 'agencia';
      return s + (saldo > 0 && !esCxc ? saldo : 0);
    }, 0);
    return { num, pax, neto, porCobrar };
  }, [filtered]);

  const rangoTexto = useMemo(() => {
    if (!fechaDesde && !fechaHasta) return 'todas las futuras';
    if (fechaDesde === fechaHasta) return fechaDesde;
    return `${fechaDesde || '—'} → ${fechaHasta || '—'}`;
  }, [fechaDesde, fechaHasta]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
            Próximas salidas
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            {proximas.length} totales · mostrando <b>{filtered.length}</b>
            {busquedaNorm ? <> · buscando <b>"{busqueda.trim()}"</b></> : <> ({rangoTexto})</>}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Búsqueda local eliminada — la barra global (BookingLayout) ya
              cubre búsqueda por texto en todas las secciones. */}
          <Button variant="outlined" startIcon={<DownloadIcon />} size="small"
            disabled={filtered.length === 0}
            onClick={() => exportarReservasCSV(filtered, `reservas-proximas-${todayLocal()}.csv`)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#0f766e', borderColor: '#0f766e' }}>
            CSV ({filtered.length})
          </Button>
        </Box>
      </Box>

      {/* KPI bar */}
      <Box sx={{
        display: 'grid', gap: 1,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        mb: 1.5,
      }}>
        <KpiTile label="Reservas" value={kpis.num}
          sub={grupos.length > 0 ? `en ${grupos.length} ${grupos.length === 1 ? 'día' : 'días'}` : '—'}
          icon={<ReceiptLongIcon />} color="#0891b2" />
        <KpiTile label="Pax total" value={kpis.pax}
          sub={kpis.num > 0 ? `${(kpis.pax / kpis.num).toFixed(1)} por reserva` : '—'}
          icon={<GroupsIcon />} color="#0d9488" />
        <KpiTile label="Ingreso esperado" value={formatPrecio(kpis.neto)}
          sub="Suma de netos Entretur"
          icon={<PaidIcon />} color="#059669" />
        <KpiTile label="Por cobrar en muelle" value={formatPrecio(kpis.porCobrar)}
          sub="Saldos del turista (excl. CxC)"
          icon={<PendingIcon />} color="#d97706" />
      </Box>

      {/* Timeline por día (colapsable) */}
      {grupos.length >= 1 && (
        <Box sx={{
          mb: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden',
        }}>
          <Box
            onClick={() => setTimelineAbierto(v => !v)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 1.25, cursor: 'pointer', userSelect: 'none',
              bgcolor: timelineAbierto ? '#f0f9ff' : '#f8fafc',
              transition: 'background 0.15s',
              '&:hover': { bgcolor: timelineAbierto ? '#e0f2fe' : '#eef2f6' },
            }}>
            <BarChartIcon sx={{ fontSize: 18, color: '#0891b2' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a',
                              textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Timeline diaria
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b', ml: 0.5 }}>
              ({grupos.length} {grupos.length === 1 ? 'día' : 'días'})
            </Typography>
            <IconButton size="small" sx={{
              ml: 'auto',
              transition: 'transform 0.18s',
              transform: timelineAbierto ? 'rotate(0deg)' : 'rotate(-90deg)',
              color: '#0891b2',
            }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Box>
          <Collapse in={timelineAbierto} timeout={200} unmountOnExit>
            <Box sx={{ p: 1.25, pt: 0.5, borderTop: '1px solid #bae6fd' }}>
              <TimelineDiaria grupos={grupos} onClickDia={toggleDia} />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Filtros: presets + fechas + agencia + paquete */}
      <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.25, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mr: 0.5,
                            textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Rango:
          </Typography>
          {PRESETS.map(p => {
            const active = presetId === p.id;
            return (
              <Chip key={p.id} size="small" clickable label={p.label}
                onClick={() => aplicarPreset(p.id)}
                sx={{
                  bgcolor: active ? '#0891b2' : '#f8fafc',
                  color: active ? '#fff' : '#475569',
                  border: `1px solid ${active ? '#0e7490' : '#e2e8f0'}`,
                  fontWeight: 600, fontSize: 11,
                  '&:hover': { bgcolor: active ? '#0e7490' : '#eef2f6' },
                }} />
            );
          })}
          {presetId === 'custom' && (
            <Chip size="small" label="Personalizado"
              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: 11 }} />
          )}
        </Box>

        <Box sx={{
          display: 'grid', gap: 1,
          // xs: apila todo; sm: 2 columnas; md: 2 columnas balanceadas
          // (evita apretar las fechas junto a 2 selects en 1024-1200px);
          // lg: fila única con las 4 secciones.
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(2, 160px) 1fr 1.2fr',
          },
          alignItems: 'center',
        }}>
          <TextField size="small" type="date" label="Desde"
            InputLabelProps={{ shrink: true }} value={fechaDesde}
            onChange={e => onFechaManual(setFechaDesde)(e.target.value)}
            sx={{ bgcolor: '#fff' }} />
          <TextField size="small" type="date" label="Hasta"
            InputLabelProps={{ shrink: true }} value={fechaHasta}
            onChange={e => onFechaManual(setFechaHasta)(e.target.value)}
            sx={{ bgcolor: '#fff' }} />
          <FormControl size="small" sx={{ bgcolor: '#fff' }}>
            <InputLabel>Agencia</InputLabel>
            <Select value={agenciaFiltro} label="Agencia"
              onChange={e => setAgenciaFiltro(e.target.value)}>
              <MenuItem value="todas"><em>Todas</em></MenuItem>
              {agenciasList.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ bgcolor: '#fff' }}>
            <InputLabel>Paquete</InputLabel>
            <Select value={filtroPaquete} label="Paquete"
              onChange={e => setFiltroPaquete(e.target.value)}>
              <MenuItem value=""><em>Todos</em></MenuItem>
              {paquetes.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Chips de estado multi-select + toggle Tarjetas/Tabla */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small" value={view} exclusive onChange={handleView}
          sx={{
            bgcolor: '#f8fafc',
            mr: 0.75,
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: 12,
              px: 1.25, py: 0.6, color: '#475569',
              border: '1px solid #e2e8f0',
              '&.Mui-selected': {
                bgcolor: '#fff', color: '#0f172a',
                boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="cards"><ViewModuleIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tarjetas</ToggleButton>
          <ToggleButton value="table"><ViewListIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tabla</ToggleButton>
        </ToggleButtonGroup>

        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mr: 0.5,
                          textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Estados:
        </Typography>
        {ESTADOS_PROX.map(est => {
          const cfg = ESTADO_LABELS[est] || { label: est, bg: '#e2e8f0', color: '#475569' };
          const active = estadosActivos.has(est);
          const n = conteosEstado[est] || 0;
          return (
            <Chip key={est} size="small" clickable
              label={
                <span>
                  {cfg.label}
                  <Box component="span" sx={{
                    ml: 0.6,
                    color: active ? cfg.color : '#94a3b8',
                    fontWeight: 700, opacity: active ? 1 : 0.7,
                  }}>{n}</Box>
                </span>
              }
              onClick={() => toggleEstado(est)}
              sx={{
                bgcolor: active ? cfg.bg : '#f8fafc',
                color: active ? cfg.color : '#94a3b8',
                border: `1px solid ${active ? cfg.color : '#e2e8f0'}`,
                fontWeight: 600, fontSize: 11,
                '&:hover': { bgcolor: active ? cfg.bg : '#eef2f6' },
              }}
              icon={
                active
                  ? <CheckCircleIcon sx={{ fontSize: '14px !important', color: `${cfg.color} !important` }} />
                  : undefined
              } />
          );
        })}
        <Button size="small"
          onClick={() => setEstadosActivos(
            estadosActivos.size === ESTADOS_PROX.length ? new Set() : new Set(ESTADOS_PROX)
          )}
          sx={{ textTransform: 'none', fontSize: 11, ml: 0.5, color: '#0891b2' }}>
          {estadosActivos.size === ESTADOS_PROX.length ? 'Ninguno' : 'Todos'}
        </Button>

        {grupos.length > 1 && (
          <Button size="small"
            startIcon={todosExpandidos
              ? <UnfoldLessIcon sx={{ fontSize: 14 }} />
              : <UnfoldMoreIcon sx={{ fontSize: 14 }} />}
            onClick={() => (todosExpandidos ? colapsarTodos() : expandirTodos())}
            sx={{ textTransform: 'none', fontSize: 11, ml: 'auto', color: '#0891b2' }}>
            {todosExpandidos ? 'Colapsar todos' : 'Expandir todos'}
          </Button>
        )}
      </Box>

      {/* Grupos por día (colapsables) */}
      {grupos.length === 0 ? (
        <Box sx={{
          p: 4, textAlign: 'center', bgcolor: '#fff',
          border: '1px dashed #cbd5e1', borderRadius: 2,
        }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
            {filtradasBase.length === 0
              ? 'Sin reservas próximas'
              : busquedaNorm
                ? 'Sin coincidencias'
                : 'Sin resultados'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>
            {filtradasBase.length === 0
              ? 'No hay reservas futuras en el rango, agencia o paquete seleccionados.'
              : busquedaNorm
                ? `Ninguna reserva coincide con "${busqueda.trim()}" en el subset filtrado.`
                : estadosActivos.size === 0
                  ? 'Ningún estado seleccionado — activá al menos uno arriba.'
                  : 'Ninguna reserva coincide con los estados seleccionados.'}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1}>
          {grupos.map(g => {
            const expandido = diasExpandidos.has(g.dia);
            // Agrupar las reservas de este día por salida (paquete + hora +
            // embarcación) — mismo helper y mismo VoyageCard que HoyPage,
            // para que la vista al expandir un día futuro se sienta idéntica
            // a la vista del día en curso.
            const { grupos: salidas, sinSalida } = agruparPorSalida(g.reservas);
            return (
              <Box key={g.dia} sx={{
                bgcolor: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 2, overflow: 'hidden',
              }}>
                <Box onClick={() => toggleDia(g.dia)}
                  sx={{
                    display: 'grid',
                    // xs: chevron + label + precio (3 col); sm+: se agregan chips
                    gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto auto auto' },
                    alignItems: 'center', gap: { xs: 1, sm: 2 },
                    p: 1.25, cursor: 'pointer', userSelect: 'none',
                    bgcolor: expandido ? '#f0f9ff' : '#f8fafc',
                    borderBottom: expandido ? '1px solid #bae6fd' : 'none',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: expandido ? '#e0f2fe' : '#eef2f6' },
                  }}>
                  <IconButton size="small" sx={{
                    transition: 'transform 0.18s',
                    transform: expandido ? 'rotate(0deg)' : 'rotate(-90deg)',
                    color: '#0891b2',
                  }}>
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                  {/* Label + sub-línea con contadores. En móvil el sub-línea
                      compensa la ausencia de los chips de "reservas / pax". */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: { xs: 13, sm: 14 }, fontWeight: 800, color: '#0f172a',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {g.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: 11, color: '#64748b',
                      display: { xs: 'block', sm: 'none' },
                    }}>
                      {g.kpis.num} {g.kpis.num === 1 ? 'reserva' : 'reservas'} · {g.kpis.pax} pax
                      {salidas.length > 0 && ` · ${salidas.length} ${salidas.length === 1 ? 'salida' : 'salidas'}`}
                    </Typography>
                  </Box>
                  <Chip size="small"
                    label={
                      salidas.length > 0
                        ? `${g.kpis.num} res · ${salidas.length} ${salidas.length === 1 ? 'salida' : 'salidas'}`
                        : `${g.kpis.num} ${g.kpis.num === 1 ? 'reserva' : 'reservas'}`
                    }
                    sx={{
                      bgcolor: '#e0f2fe', color: '#0369a1',
                      fontWeight: 700, fontSize: 11, height: 22,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }} />
                  <Chip size="small"
                    label={`${g.kpis.pax} pax`}
                    sx={{
                      bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 700, fontSize: 11, height: 22,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }} />
                  <Typography sx={{
                    fontSize: 13, fontWeight: 800, color: '#059669',
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                    fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                  }}>
                    {formatPrecio(g.kpis.neto)}
                  </Typography>
                </Box>

                <Collapse in={expandido} timeout={180} unmountOnExit>
                  <Box sx={{ p: 1.25, pt: 0.75 }}>
                    {salidas.length > 0 ? (
                      <Stack spacing={1}>
                        {salidas.map(s => (
                          <VoyageCard
                            key={s.salida_id}
                            hora={s.hora}
                            paquete={s.paquete}
                            lanchas={s.lanchas}
                            capacidad={s.capacidad}
                            ocupados={s.ocupados}
                            reservas={s.reservas}
                            view={view}
                            onRowClick={r => setDetalleId(r.id)}
                            onDelete={r => setBorrarReserva(r)}
                            horaCutoff={horaCutoff}
                          />
                        ))}
                        {sinSalida.length > 0 && (
                          <Box sx={{
                            bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5,
                          }}>
                            <Typography sx={{
                              fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
                              fontWeight: 700, color: '#64748b', mb: 1,
                            }}>
                              Sin salida asignada · {sinSalida.length}
                            </Typography>
                            <Box sx={{ overflowX: 'auto' }}>
                              <ReservacionesTable
                                rows={sinSalida}
                                loading={false}
                                onRowClick={r => setDetalleId(r.id)}
                                onDelete={r => setBorrarReserva(r)}
                                horaCutoff={horaCutoff}
                                showFecha={false}
                                hideSearch
                                emptyTitle="" emptyMessage=""
                              />
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      // Todas las reservas del día quedaron sin salida (raro pero
                      // posible con alquileres/walk-in). Cae a la tabla clásica.
                      <Box sx={{ overflowX: 'auto' }}>
                        <ReservacionesTable
                          rows={g.reservas}
                          loading={false}
                          onRowClick={r => setDetalleId(r.id)}
                          onDelete={r => setBorrarReserva(r)}
                          horaCutoff={horaCutoff}
                          defaultSortBy="salida_hora"
                          defaultSortDir="asc"
                          hideSearch
                          emptyTitle=""
                          emptyMessage=""
                        />
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}

      {detalleId && (
        <ReservaSlideOver reservaId={detalleId}
          onClose={() => setDetalleId(null)} onUpdated={refetch} />
      )}

      <BorrarReservaDialog
        reserva={borrarReserva}
        open={!!borrarReserva}
        onClose={() => setBorrarReserva(null)}
        onDeleted={() => { setBorrarReserva(null); refetch(); }}
      />
    </Box>
  );
}
